/**
 * Pure client ticket-create access evaluation — shared by the server gate,
 * client-side pre-check, and unit tests. Mirrors
 * `client_can_create_ticket_for_contact()` in
 * `20241201000000_baseline.sql` (:966). The originating migration
 * `20260619130000_client_access_levels_ticket_create_gate.sql` is under
 * `_archive_pre_baseline/`.
 */

import { ACCESS_LEVEL, canCreateTicketsAtLevel } from "@/lib/auth/access-levels";

export type AccessGrantRow = {
  access_level: string;
  is_active: boolean;
  expires_at: string | null;
};

/** Raw columns from `contact_user_access` / `company_user_access` selects. */
export type AccessGrantRowInput = {
  access_level: string | null;
  is_active: boolean | null;
  expires_at: string | null;
};

export function normalizeAccessGrantRows(
  rows: AccessGrantRowInput[] | null | undefined,
): AccessGrantRow[] {
  if (!rows?.length) return [];
  return rows.flatMap((row) => {
    if (row.access_level == null) return [];
    return [
      {
        access_level: row.access_level,
        is_active: row.is_active ?? false,
        expires_at: row.expires_at,
      },
    ];
  });
}

export type ClientTicketCreateDenialReason =
  | "allowed"
  | "missing_contact"
  | "no_grant"
  | "inactive_grant"
  | "expired_grant"
  | "read_only_contact"
  | "read_only_company"
  | "lookup_failed"
  | "allowed_but_blocked";

function isGrantActive(grant: AccessGrantRow, nowIso: string): boolean {
  return (
    grant.is_active === true &&
    (grant.expires_at == null || grant.expires_at > nowIso)
  );
}

function grantAllowsCreate(grant: AccessGrantRow, nowIso: string): boolean {
  return isGrantActive(grant, nowIso) && canCreateTicketsAtLevel(grant.access_level as never);
}

export function evaluateClientTicketCreateAccess(input: {
  contactGrants: AccessGrantRow[];
  companyGrants: AccessGrantRow[];
  companyId: string | null;
  now?: Date;
}): ClientTicketCreateDenialReason {
  const nowIso = (input.now ?? new Date()).toISOString();
  const { contactGrants, companyGrants, companyId } = input;

  if ((contactGrants ?? []).some((g) => grantAllowsCreate(g, nowIso))) {
    return "allowed";
  }

  if (
    companyId &&
    (companyGrants ?? []).some((g) => grantAllowsCreate(g, nowIso))
  ) {
    return "allowed";
  }

  const activeContactGrants = (contactGrants ?? []).filter((g) => isGrantActive(g, nowIso));
  const activeCompanyGrants = (companyGrants ?? []).filter((g) => isGrantActive(g, nowIso));

  if (
    activeContactGrants.some((g) => g.access_level === ACCESS_LEVEL.READ_ONLY) &&
    !activeContactGrants.some((g) => canCreateTicketsAtLevel(g.access_level as never))
  ) {
    return "read_only_contact";
  }

  if (
    companyId &&
    activeCompanyGrants.some((g) => g.access_level === ACCESS_LEVEL.READ_ONLY) &&
    !activeCompanyGrants.some((g) => canCreateTicketsAtLevel(g.access_level as never))
  ) {
    return "read_only_company";
  }

  const allGrants = [...(contactGrants ?? []), ...(companyGrants ?? [])];
  if (allGrants.length > 0 && activeContactGrants.length === 0 && activeCompanyGrants.length === 0) {
    const hasExpired = allGrants.some(
      (g) => g.is_active === true && g.expires_at != null && g.expires_at <= nowIso,
    );
    if (hasExpired) return "expired_grant";
    const hasInactive = allGrants.some((g) => g.is_active === false);
    if (hasInactive) return "inactive_grant";
  }

  return "no_grant";
}

export function formatClientTicketCreateDenialMessage(
  reason: ClientTicketCreateDenialReason,
): string {
  switch (reason) {
    case "allowed":
      return "";
    case "missing_contact":
      return "Select a contact before creating a ticket.";
    case "read_only_contact":
      return "Your access to this contact is read-only. You can view tickets but not create new ones — contact WTC to request ticket creation access.";
    case "read_only_company":
      return "Your company access is read-only. You can view tickets but not create new ones — contact WTC to request ticket creation access.";
    case "inactive_grant":
      return "Your access grant has been deactivated. Contact WTC if you believe this is an error.";
    case "expired_grant":
      return "Your access grant has expired. Contact WTC to renew access.";
    case "no_grant":
      return "You don't have access to create tickets for this contact. Choose a contact you have been granted access to, or contact WTC.";
    case "lookup_failed":
      return "We could not verify your ticket creation access. Please try again or contact WTC support.";
    case "allowed_but_blocked":
      return "You should be able to create tickets for this contact, but the request was blocked. Please contact WTC support — this may be a configuration issue.";
    default: {
      const _exhaustive: never = reason;
      return _exhaustive;
    }
  }
}
